import type { HTMLAttributes, ReactNode } from "react";
import styles from "./TopNavigation.module.css";
import { cx } from "../cx";
import { IconChevronLeftLine, IconDot3HorizontalLine } from "../icons/Icon";
import { BrickPill } from "./BrickPill";

type Base = HTMLAttributes<HTMLElement> & {
  tone?: "layer" | "transparent";
  /** Left slot. `undefined` = back chevron, `null` = keep 44px empty space. */
  left?: ReactNode | null;
  /** Right slot. `undefined` = ⋯ menu, `null` = keep 44px empty space. */
  right?: ReactNode | null;
  onBack?: () => void;
  onMenu?: () => void;
};

export type TopNavigationProps = Base &
  (
    | { type?: "default"; title: ReactNode }
    | { type: "pageTitle"; title: ReactNode; brickCount?: number | string }
    | { type: "logo"; logo: ReactNode; brickCount?: number | string }
    | { type: "chat"; title: ReactNode; subtitle?: ReactNode; avatar?: ReactNode }
    | { type: "search"; search: ReactNode }
  );

/** SEED TopNavigation (Figma 95:3605). 56px, 아이콘 버튼 44, 좌우 패딩 6. */
export function TopNavigation(props: TopNavigationProps) {
  // variant-specific props는 DOM으로 새지 않게 여기서 걷어낸다.
  const {
    tone = "layer", left, right, onBack, onMenu, className,
    type: _t, title: _ti, subtitle: _s, avatar: _a, brickCount: _b, logo: _l, search: _se,
    ...rest
  } = props as Base & { type?: string; title?: unknown; subtitle?: unknown; avatar?: unknown; brickCount?: unknown; logo?: unknown; search?: unknown };
  const type = props.type ?? "default";

  const leftNode =
    left === undefined ? (
      <button type="button" className={styles.iconButton} aria-label="뒤로" onClick={onBack}>
        <IconChevronLeftLine />
      </button>
    ) : (
      left
    );
  const rightNode =
    right === undefined ? (
      <button type="button" className={styles.iconButton} aria-label="메뉴" onClick={onMenu}>
        <IconDot3HorizontalLine />
      </button>
    ) : (
      right
    );

  const showLeft = type === "default" || type === "chat" || type === "search";

  return (
    <header className={cx(styles.root, styles[tone], styles[type], className)} {...rest}>
      {showLeft && <div className={styles.slot}>{leftNode}</div>}

      {props.type === undefined || props.type === "default" ? (
        <h1 className={cx(styles.title, styles.centerTitle)}>{props.title}</h1>
      ) : null}

      {props.type === "pageTitle" && (
        <>
          <h1 className={cx(styles.title, styles.pageTitleText)}>{props.title}</h1>
          {props.brickCount !== undefined && <BrickPill count={props.brickCount} />}
        </>
      )}

      {props.type === "logo" && (
        <>
          <div className={styles.logo}>{props.logo}</div>
          <div className={styles.spacer} />
          {props.brickCount !== undefined && <BrickPill count={props.brickCount} />}
        </>
      )}

      {props.type === "chat" && (
        <div className={styles.chatMain}>
          {props.avatar && <div className={styles.avatar}>{props.avatar}</div>}
          <div className={styles.titles}>
            <p className={styles.chatTitle}>{props.title}</p>
            {props.subtitle && <p className={styles.chatSubtitle}>{props.subtitle}</p>}
          </div>
        </div>
      )}

      {props.type === "search" && <div className={styles.search}>{props.search}</div>}

      {type !== "search" && <div className={styles.slot}>{rightNode}</div>}
    </header>
  );
}
